import { addOfficerPreservingAssignments, assignmentRows } from './assignments';
import { SkeletonCards } from '../../components/LoadingSkeleton/LoadingSkeleton';
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { 
  MdSearch, 
  MdPerson, 
  MdBusiness, 
  MdAdd, 
  MdClose, 
  MdCheckCircle
} from "react-icons/md";
import "./clientofficer.css";
import TrashIcon from '../../components/icons/TrashIcon';

const PAGE_SIZE = 10;
const ListPagination = ({ page, total, onChange, label }) => {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (!total) return null;
  return <nav className="co-pagination" aria-label={label}>
    <span>{(page - 1) * PAGE_SIZE + 1}&ndash;{Math.min(page * PAGE_SIZE, total)} of {total}</span>
    <div><button disabled={page <= 1} onClick={() => onChange(page - 1)}>Previous</button><span>Page {page} of {pages}</span><button disabled={page >= pages} onClick={() => onChange(page + 1)}>Next</button></div>
  </nav>;
};

const ClientOfficer = () => {
  const [directoryPage, setDirectoryPage] = useState(1);
  const [assignmentPage, setAssignmentPage] = useState(1);
  const [modalPage, setModalPage] = useState(1);
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("admin_user") || "{}"));
  const [viewMode, setViewMode] = useState("clients-to-offices");
  
  const isDealer276 = String(user?.dealerId) === "276" || localStorage.getItem("dealerId") === "276" || user?.role === "Admin" || user?.role === "superadmin";
  
  // Lists
  const [clients, setClients] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [clientsofficers, setClientsofficers] = useState([]);
  const [officerclients, setOfficerclients] = useState([]);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [remainingItems, setRemainingItems] = useState([]);
  const [modalSearch, setModalSearch] = useState("");

  // Loading States
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const assignmentInFlight = useRef(false);
  const assignmentRequest = useRef(0);

  // Search States
  const [leftSearch, setLeftSearch] = useState("");
  const [rightSearch, setRightSearch] = useState("");
  
  // Selection States
  const [selectedItem, setSelectedItem] = useState(null);

  const getValue = (item, keys) => {
    for (const key of keys) {
      const value = item?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }
    return "";
  };

  const normalizeId = (value) => String(value ?? "").trim();
  const getClientId = (item) => getValue(item, ["clientId", "client_id", "id"]);
  const getOfficeId = (item) => getValue(item, ["officeId", "office_id", "id"]);

  const getClientName = (item) => {
    const clientName = getValue(item, ["client", "clientName", "rclient", "name", "fullName"]);
    if (clientName) return clientName;

    return [
      getValue(item, ["firstName", "firstname", "first_name"]),
      getValue(item, ["lastName", "lastname", "last_name"]),
    ].filter(Boolean).join(" ");
  };

  const getOfficeName = (item) => getValue(item, ["officeName", "office", "officer", "name"]);

  const getItemKey = (item, type, index) => {
    const keyParts = type === "client"
      ? [getClientId(item), item.sno, item.cbid, getClientName(item)]
      : [getOfficeId(item), getOfficeName(item)];

    const stablePart = keyParts
      .map((part) => normalizeId(part))
      .filter(Boolean)
      .join("-");

    return `${type}-${stablePart || "row"}-${index}`;
  };

  const getDisplayName = (item, type) => {
    if (type === "client") {
      return getClientName(item) || "Unnamed Client";
    }
    return getOfficeName(item) || "Unnamed Office";
  };

  const getDisplaySubtext = (item) => getValue(item, [
    "email",
    "clientemail",
    "clientEmail",
    "phone",
    "mobile",
    "clientphone1",
    "clientAddress",
    "sno",
    "cbid",
    "devId",
    "deviceId",
    "controlId",
  ]);

  const getItemSearchText = (item) => [
    item.officeName,
    item.office,
    item.officer,
    item.client,
    item.clientName,
    item.rclient,
    item.clientemail,
    item.clientEmail,
    item.clientphone1,
    item.clientAddress,
    item.firstName,
    item.firstname,
    item.first_name,
    item.lastName,
    item.lastname,
    item.last_name,
    item.name,
    item.fullName,
    item.email,
    item.phone,
    item.mobile,
    item.sno,
    item.cbid,
    item.devId,
    item.controlId,
    item.deviceId,
    item.id,
    item.clientId,
    item.officeId,
  ].filter(Boolean).join(" ").toLowerCase();

  // Helper: Format Date for Input (YYYY-MM-DD)
  const formatDateForInput = (date) => {
    if (!date) return "";
    const parts = date.split("/");
    if (parts.length !== 3) return date;
    return `${parts[2]}-${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}`;
  };

  const formatDateForAPI = (date) => {
    if (!date) return "";
    const parts = date.split("-");
    if (parts.length !== 3) return date;
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  };

  // Helper: Map dates for office items
  const mapOfficeDates = useCallback((offices) => {
    return (offices || []).map((item) => ({
      ...item,
      rstartdate: formatDateForInput(item.Rstartdate || item.rstartdate),
      renddate: formatDateForInput(item.Renddate || item.renddate),
    }));
  }, []);

  const showSnackbar = (message, type) => {
    Swal.fire({
      text: message,
      icon: type,
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  };

  // Fetch Initial Data
  const fetchInitialData = useCallback(async () => {
    const loggedUser = JSON.parse(localStorage.getItem("admin_user")) || {};
    setUser(loggedUser);
    setIsLoading(true);

    try {
      if (loggedUser.role === "Admin" || loggedUser.role === "superadmin") {
        const [clientsResp, officersResp] = await Promise.all([
          axios.get("https://alcobrakeadmin.com/v3/us/fullclients/getallfullrclients"),
          axios.get("https://alcobrakeadmin.com/v3/us/caoffices/getalloffices")
        ]);
        setClients(clientsResp.data || []);
        setOfficers(officersResp.data || []);
      } else {
        const [clientsResp, officersResp] = await Promise.all([
          axios.get(`https://alcobrakeadmin.com/v3/us/fullclients/getdealerfullrclients?dealerName=${loggedUser.dealerId}`),
          axios.get(`https://alcobrakeadmin.com/v3/us/caoffices/getdealeroffices?dealerName=${loggedUser.dealerId}`)
        ]);
        setClients(clientsResp.data || []);
        setOfficers(officersResp.data || []);
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Load Assigned List
  const loadAssignedList = async (item, mode = viewMode) => {
    const request = ++assignmentRequest.current;
    setIsRefreshing(true);
    try {
      if (mode === "officer-to-clients") {
        const resp = await axios.get(
          `https://alcobrakeadmin.com/v3/us/fullclients/getofficefullclients?officeId=${getOfficeId(item)}`
        );
        if (request !== assignmentRequest.current) return;
        setClientsofficers([]);
        setOfficerclients(assignmentRows(resp.data));
      } else {
        const resp = await axios.get(
          `https://alcobrakeadmin.com/v3/us/fullclients/getclientoffices?id=${encodeURIComponent(getClientId(item))}`
        );
        if (request !== assignmentRequest.current) return;
        setOfficerclients([]);
        setClientsofficers(mapOfficeDates(assignmentRows(resp.data)));
      }
    } catch (error) {
      console.error("Error fetching assigned list:", error);
    } finally {
      if (request === assignmentRequest.current) setIsRefreshing(false);
    }
  };

  const handleSelectItem = (item) => {
    if (assignmentInFlight.current) return;
    setAssignmentPage(1);
    setRightSearch("");
    setSelectedItem(item);
    loadAssignedList(item, viewMode);
  };

  const handleViewModeChange = (mode) => {
    if (assignmentInFlight.current) return;
    assignmentRequest.current += 1;
    setIsRefreshing(false);
    setDirectoryPage(1);
    setAssignmentPage(1);
    setViewMode(mode);
    setSelectedItem(null);
    setLeftSearch("");
    setRightSearch("");
    setClientsofficers([]);
    setOfficerclients([]);
  };

  // Modal Handlers
  const openAddModal = () => {
    if (!selectedItem || isRefreshing || assignmentInFlight.current) return;
    
    let available = [];
    if (viewMode === "officer-to-clients") {
      available = clients.filter(c => !officerclients.some(oc => normalizeId(getClientId(oc)) === normalizeId(c.id)));
    } else {
      available = officers.filter(o => !clientsofficers.some(co => normalizeId(getOfficeId(co)) === normalizeId(o.officeId)));
    }
    
    setModalPage(1);
    setModalSearch(""); // Clear search when opening
    setRemainingItems(available);
    setShowAddModal(true);
  };

  // Actions
  const handleAssign = async (targetItem) => {
    if (assignmentInFlight.current || !selectedItem) return;
    assignmentInFlight.current = true;
    setIsActionLoading(true);
    try {
      const client = viewMode === "officer-to-clients" ? targetItem : selectedItem;
      const office = viewMode === "officer-to-clients" ? selectedItem : targetItem;
      const saved = await addOfficerPreservingAssignments(getClientId(client), getOfficeId(office), client.installDate);
      if (viewMode === "clients-to-offices") setClientsofficers(mapOfficeDates(saved));
      else await loadAssignedList(selectedItem);
      setRightSearch("");
      setAssignmentPage(1);
      setShowAddModal(false);
      showSnackbar("Assigned successfully. Existing officers retained.", "success");
    } catch (error) {
      showSnackbar(error.message || "Assignment failed", "error");
      await loadAssignedList(selectedItem);
    } finally {
      assignmentInFlight.current = false;
      setIsActionLoading(false);
    }
  };

  const handleRemove = async (item) => {
    const result = await Swal.fire({
      title: "Remove Assignment?",
      text: `Are you sure you want to remove this ${viewMode === "officer-to-clients" ? "client" : "office"}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, remove",
    });

    if (result.isConfirmed) {
      try {
        const clientId = viewMode === "officer-to-clients" ? getClientId(item) : getClientId(selectedItem);
        const officeId = viewMode === "officer-to-clients" ? getOfficeId(selectedItem) : getOfficeId(item);

        await axios.post("https://alcobrakeadmin.com/v3/us/fullclients/deleteclientoffice", {
          client_id: String(clientId),
          office_id: String(officeId),
        });

        showSnackbar("Removed successfully", "success");
        loadAssignedList(selectedItem);
      } catch (error) {
        showSnackbar("Removal failed", "error");
      }
    }
  };

  const handleUpdatePrimary = async (item) => {
    try {
      const payload = {
        client_id: getClientId(selectedItem),
        office_id: getOfficeId(item),
        rstartdate: formatDateForAPI(item.rstartdate),
        renddate: formatDateForAPI(item.renddate),
      };

      await axios.post("https://alcobrakeadmin.com/v3/us/fullclients/updateprimaryoffice", null, {
        params: payload,
      });

      setClientsofficers(prev => prev.map(o => ({
        ...o,
        primary_office: normalizeId(getOfficeId(o)) === normalizeId(getOfficeId(item))
      })));
      
      showSnackbar("Updated successfully", "success");
    } catch (error) {
      showSnackbar("Update failed", "error");
    }
  };

  // Filters
const filteredLeftItems = (viewMode === "officer-to-clients" ? officers : clients).filter(item => {
  const searchText = JSON.stringify(item).toLowerCase();
  return searchText.includes(leftSearch.trim().toLowerCase());
});

  const filteredAssignedItems = (viewMode === "officer-to-clients" ? officerclients : clientsofficers).filter(item => {
    return getItemSearchText(item).includes(rightSearch.trim().toLowerCase());
  });

  const filteredModalItems = remainingItems.filter(item => {
    return getItemSearchText(item).includes(modalSearch.trim().toLowerCase());
  });

  const currentDirectoryPage = Math.min(directoryPage, Math.max(1, Math.ceil(filteredLeftItems.length / PAGE_SIZE)));
  const currentAssignmentPage = Math.min(assignmentPage, Math.max(1, Math.ceil(filteredAssignedItems.length / PAGE_SIZE)));
  const currentModalPage = Math.min(modalPage, Math.max(1, Math.ceil(filteredModalItems.length / PAGE_SIZE)));

  return (
    <div className="clientofficer-container">
      <div className="clientofficer-card">
        {/* Header */}
        <div className="clientofficer-header">
          <h1 className="clientofficer-title">Client to Officer Managements</h1>
          <p className="clientofficer-subtitle">Bridge the connection between clients and offices</p>
        </div>

        {/* View Toggle */}
        <div className="view-toggle">
          <button 
            className={`toggle-btn ${viewMode === "clients-to-offices" ? "active" : ""}`}
            onClick={() => handleViewModeChange("clients-to-offices")}
          >
            <MdPerson size={18} />
            Clients to Officers
          </button>
          <button 
            className={`toggle-btn ${viewMode === "officer-to-clients" ? "active" : ""}`}
            onClick={() => handleViewModeChange("officer-to-clients")}
          >
            <MdBusiness size={18} />
            Officers to Clients
          </button>
        </div>

        <div className="two-column-layout">
          {/* Left Side: Directory */}
          <div className="left-panel">
            <div className="panel-header">
              <div className="panel-title">
                {viewMode === "officer-to-clients" ? "Office Directory" : "Client Directory"}
              </div>
              <div className="search-wrapper">
                <MdSearch className="search-icon-svg" />
                <input 
                  type="text" 
                  placeholder="Search..." 
                  className="search-input"
                  value={leftSearch}
                  onChange={(e) => { setLeftSearch(e.target.value); setDirectoryPage(1); }}
                />
              </div>
            </div>
            
            <div className="scrollable-list">
              {isLoading ? (
                <SkeletonCards className="co-directory-skeleton" itemClassName="item-card" label={viewMode === "officer-to-clients" ? "Loading office directory..." : "Loading client directory..."} />
              ) : filteredLeftItems.length === 0 ? (
                <div className="empty-placeholder"><p>No results found</p></div>
              ) : (
                filteredLeftItems.slice((currentDirectoryPage - 1) * PAGE_SIZE, currentDirectoryPage * PAGE_SIZE).map((item, index) => (
                  <button 
                    key={getItemKey(item, viewMode === "officer-to-clients" ? "office" : "client", index)} 
                    className={`item-card ${(viewMode === "officer-to-clients" && normalizeId(getOfficeId(selectedItem)) === normalizeId(getOfficeId(item))) || (viewMode === "clients-to-offices" && normalizeId(getClientId(selectedItem)) === normalizeId(getClientId(item))) ? "active" : ""}`}
                    onClick={() => handleSelectItem(item)}
                  >
                    <div className="avatar">
                      {viewMode === "officer-to-clients" ? <MdBusiness /> : <MdPerson />}
                    </div>
                    <div className="item-info">
                      <div className="item-name">
                        {getDisplayName(item, viewMode === "officer-to-clients" ? "office" : "client")}
                      </div>
                      <div className="item-subtext">{getDisplaySubtext(item)}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
            {!isLoading && <ListPagination page={currentDirectoryPage} total={filteredLeftItems.length} onChange={setDirectoryPage} label="Directory pagination" />}
          </div>

          {/* Right Side: Assignments */}
          <div className="right-panel">
            <div className="panel-header">
              <div className="panel-title">
                <span>Assignments {selectedItem && <span className="assigned-count">({filteredAssignedItems.length})</span>}</span>
                {selectedItem && viewMode !== "officer-to-clients" && (
                  <button className="add-btn-main" disabled={isRefreshing || isActionLoading} onClick={openAddModal}>
                    <MdAdd style={{ marginRight: "4px" }} /> Assign New
                  </button>
                )}
              </div>
              {selectedItem && (
                <div className="search-wrapper" style={{ marginTop: "1rem" }}>
                  <MdSearch className="search-icon-svg" />
                  <input 
                    type="text" 
                    placeholder="Filter assigned items..." 
                    className="search-input"
                    value={rightSearch}
                    onChange={(e) => { setRightSearch(e.target.value); setAssignmentPage(1); }}
                  />
                </div>
              )}
            </div>

            {!selectedItem ? (
              <div className="empty-placeholder">
                <div className="empty-icon">👈</div>
                <h5>Select from directory</h5>
                <h6>Choose an item from the left panel to manage its connections.</h6>
              </div>
            ) : (
              <div className="assigned-section">
                <div className="section-label">Currently Assigned</div>
                
                {isRefreshing ? (
                  <SkeletonCards className="assigned-grid" itemClassName="assigned-card" rows={4} label="Loading assignments..." />
                ) : filteredAssignedItems.length === 0 ? (
                  <div className="empty-placeholder" style={{ padding: "3rem" }}>
                    <p>No assignments found for this selection.</p>
                  </div>
                ) : (
                  <div className="assigned-grid">
                    {filteredAssignedItems.slice((currentAssignmentPage - 1) * PAGE_SIZE, currentAssignmentPage * PAGE_SIZE).map((item, index) => (
                      <div
                        key={getItemKey(item, viewMode === "officer-to-clients" ? "client" : "office", index)}
                        className="assigned-card"
                      >
                        <div className="assigned-header">
                          <div className="item-info">
                            <div className="item-name">
                              {getDisplayName(item, viewMode === "officer-to-clients" ? "client" : "office")}
                              {item.primary_office && <span className="primary-badge" style={{ marginLeft: "8px" }}>Primary</span>}
                            </div>
                            <div className="item-subtext">{getDisplaySubtext(item)}</div>
                          </div>
                          <div className="action-btns">
                            {viewMode === "clients-to-offices" && (
                              <button 
                                className="btn-icon" 
                                onClick={() => handleUpdatePrimary(item)}
                                title="Set as Primary"
                                style={{ color: item.primary_office ? "var(--success-color)" : "inherit" }}
                              >
                                <MdCheckCircle />
                              </button>
                            )}
                            {/* <button 
                              className="btn-icon danger" 
                              onClick={() => handleRemove(item)}
                              title="Remove Assignment"
                              disabled={item.primary_office}
                            >
                              <TrashIcon />
                            </button> */}
                          </div>
                        </div>

                        {/* Special Logic for Dealer 276 */}
                        {viewMode === "clients-to-offices" && isDealer276 && (
                          <div className="date-row">
                            <div className="date-field">
                              <label>Report Start</label>
                              <input 
                                type="date" 
                                className="date-input"
                                value={item.rstartdate || ""}
                                onChange={(e) => { item.rstartdate = e.target.value; handleUpdatePrimary(item); }}
                              />
                            </div>
                            <div className="date-field">
                              <label>Report End</label>
                              <input 
                                type="date" 
                                className="date-input"
                                value={item.renddate || ""}
                                onChange={(e) => { item.renddate = e.target.value; handleUpdatePrimary(item); }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {selectedItem && !isRefreshing && <ListPagination page={currentAssignmentPage} total={filteredAssignedItems.length} onChange={setAssignmentPage} label="Assignment pagination" />}
          </div>
        </div>
      </div>

      {/* Add Assignment Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-title">
                Assign {viewMode === "officer-to-clients" ? "Client" : "Office"}
              </div>
              <button className="btn-icon" disabled={isActionLoading} onClick={() => setShowAddModal(false)}>
                <MdClose />
              </button>
            </div>
            <div className="panel-header" style={{ borderBottom: "none" }}>
              <div className="search-wrapper">
                <MdSearch className="search-icon-svg" />
                <input 
                  type="text" 
                  placeholder="Search available items..." 
                  className="search-input"
                  value={modalSearch}
                  onChange={(e) => { setModalSearch(e.target.value); setModalPage(1); }}
                />
              </div>
            </div>
            <div className="modal-body scrollable-list">
              {filteredModalItems.length === 0 ? (
                <div className="empty-placeholder"><p>No items available to assign.</p></div>
              ) : (
                filteredModalItems.slice((currentModalPage - 1) * PAGE_SIZE, currentModalPage * PAGE_SIZE).map((item, index) => (
                  <div
                    key={getItemKey(item, viewMode === "officer-to-clients" ? "client" : "office", index)}
                    className="item-card"
                    style={{ border: "1px solid var(--border-color)", cursor: "default" }}
                  >
                    <div className="avatar">
                      {viewMode === "officer-to-clients" ? <MdPerson /> : <MdBusiness />}
                    </div>
                    <div className="item-info">
                      <div className="item-name">
                        {getDisplayName(item, viewMode === "officer-to-clients" ? "client" : "office")}
                      </div>
                      <div className="item-subtext">{getDisplaySubtext(item)}</div>
                    </div>
                    <button className="btn-icon" disabled={isActionLoading} aria-label={`Assign ${getDisplayName(item, viewMode === "officer-to-clients" ? "client" : "office")}`} onClick={() => handleAssign(item)} style={{ color: "var(--primary-color)" }}>
                      <MdAdd />
                    </button>
                  </div>
                ))
              )}
            </div>
            <ListPagination page={currentModalPage} total={filteredModalItems.length} onChange={setModalPage} label="Available assignments pagination" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientOfficer;
